/**
 * Verified-purchase reviews + open Q&A comments service.
 *
 * Reviews come from shop.vendor_reviews / shop.product_reviews behind
 * the rpc_submit_vendor_review / rpc_submit_product_review RPCs so
 * we never have to materialise the verified_via_order_id lookup in the
 * client. Comments come from shop.vendor_comments / shop.product_comments
 * (Phase 1) and are open to any authenticated user.
 *
 * Reads split by audience:
 *   * `list*ReviewsPublic`  -- anon + buyers (published, non-archived, via view).
 *   * `getMy*Review`        -- authenticated reviewers retrieving their own row.
 *   * `list*CommentsPublic` -- anon + buyers (non-deleted, non-archived, via view).
 *
 * Verified-buyer gating helpers (`canReview*`) are issued through
 * security-definer RPCs so the buyer can check eligibility without
 * needing direct read access on shop.orders.
 */

import { supabase } from '../client';
import {
  applyKeyset,
  buildNextCursor,
  clampPageSize,
  selectCountOption,
  type KeysetInput,
  type ListResult,
} from '../pagination';
import type {
  ShopProductCommentPublic,
  ShopProductReview,
  ShopProductReviewPublic,
  ShopVendorCommentPublic,
  ShopVendorReview,
  ShopVendorReviewPublic,
} from './shopTypes';

// ---- Reviews ------------------------------------------------------------

export interface ListReviewsInput extends KeysetInput {
  vendorId?: string;
  productId?: string;
}

export async function listShopVendorReviewsPublic(
  input: ListReviewsInput & { vendorId: string },
): Promise<ListResult<ShopVendorReviewPublic>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('v_vendor_reviews_public')
    .select('*', selectCountOption(input.countMode))
    .eq('vendor_id', input.vendorId);

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopVendorReviewPublic[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function listShopProductReviewsPublic(
  input: ListReviewsInput & { productId: string },
): Promise<ListResult<ShopProductReviewPublic>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('v_product_reviews_public')
    .select('*', selectCountOption(input.countMode))
    .eq('product_id', input.productId);

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopProductReviewPublic[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function getMyShopVendorReview(
  vendorId: string,
): Promise<ShopVendorReview | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_reviews')
    .select('*')
    .eq('vendor_id', vendorId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopVendorReview | null;
}

export async function getMyShopProductReview(
  productId: string,
): Promise<ShopProductReview | null> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('product_reviews')
    .select('*')
    .eq('product_id', productId)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as ShopProductReview | null;
}

// ---- Verified-buyer gating ---------------------------------------------

export async function canReviewVendor(input: {
  buyerUserId: string;
  vendorId: string;
}): Promise<boolean> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('fn_buyer_can_review_vendor', {
      p_buyer_user_id: input.buyerUserId,
      p_vendor_id: input.vendorId,
    });
  if (error) throw error;
  return Boolean(data);
}

export async function canReviewProduct(input: {
  buyerUserId: string;
  productId: string;
}): Promise<boolean> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('fn_buyer_can_review_product', {
      p_buyer_user_id: input.buyerUserId,
      p_product_id: input.productId,
    });
  if (error) throw error;
  return Boolean(data);
}

// ---- Submit / upsert RPCs ----------------------------------------------

export interface SubmitReviewInput {
  rating: number;
  title?: string | null;
  bodyMd: string;
}

export async function submitShopVendorReview(
  input: SubmitReviewInput & { vendorId: string },
): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_submit_vendor_review', {
      p_vendor_id: input.vendorId,
      p_rating: input.rating,
      p_title: input.title ?? null,
      p_body_md: input.bodyMd,
    });
  if (error) throw error;
  return data as string;
}

export async function submitShopProductReview(
  input: SubmitReviewInput & { productId: string },
): Promise<string> {
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .rpc('rpc_submit_product_review', {
      p_product_id: input.productId,
      p_rating: input.rating,
      p_title: input.title ?? null,
      p_body_md: input.bodyMd,
    });
  if (error) throw error;
  return data as string;
}

// ---- Comments (Q&A) ----------------------------------------------------

export async function listShopVendorCommentsPublic(
  input: KeysetInput & { vendorId: string },
): Promise<ListResult<ShopVendorCommentPublic>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('v_vendor_comments_public')
    .select('*', selectCountOption(input.countMode))
    .eq('vendor_id', input.vendorId);

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopVendorCommentPublic[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

export async function listShopProductCommentsPublic(
  input: KeysetInput & { productId: string },
): Promise<ListResult<ShopProductCommentPublic>> {
  const pageSize = clampPageSize(input.pageSize);
  let q = (supabase() as any)
    .schema('shop')
    .from('v_product_comments_public')
    .select('*', selectCountOption(input.countMode))
    .eq('product_id', input.productId);

  q = applyKeyset(q, input.cursor, 'created_at');
  q = q.limit(pageSize);

  const { data, error, count } = await q;
  if (error) throw error;
  const rows = (data ?? []) as ShopProductCommentPublic[];
  return {
    rows,
    nextCursor: buildNextCursor(
      rows as unknown as Array<Record<string, unknown>>,
      pageSize,
      'created_at',
    ),
    total: typeof count === 'number' ? count : null,
    totalIsExact: input.countMode === 'exact',
  };
}

async function currentUserId(): Promise<string> {
  const { data, error } = await supabase().auth.getUser();
  if (error) throw error;
  const userId = data.user?.id;
  if (!userId) {
    throw new Error('You must be signed in to post a comment.');
  }
  return userId;
}

export async function postShopVendorComment(input: {
  vendorId: string;
  bodyMd: string;
  parentId?: string | null;
}): Promise<string> {
  const authorUserId = await currentUserId();
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_comments')
    .insert({
      vendor_id: input.vendorId,
      author_user_id: authorUserId,
      body_md: input.bodyMd,
      parent_id: input.parentId ?? null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function postShopProductComment(input: {
  productId: string;
  bodyMd: string;
  parentId?: string | null;
}): Promise<string> {
  const authorUserId = await currentUserId();
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('product_comments')
    .insert({
      product_id: input.productId,
      author_user_id: authorUserId,
      body_md: input.bodyMd,
      parent_id: input.parentId ?? null,
    })
    .select('id')
    .single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export async function softDeleteShopVendorComment(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('vendor_comments')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function softDeleteShopProductComment(id: string): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('shop')
    .from('product_comments')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
